import { InterestBand, InterestRate, InterestSettings } from '../types'
import { addDays, toISODate } from './dateRules'

/**
 * Savings interest: tiered bands, accrued daily, paid monthly.
 *
 * Each day earns interest on that day's closing balance at the rate in force
 * that day, as annual rate ÷ 365 — the convention most UK savings accounts
 * use, leap years included. What has accrued is paid in on the payment day,
 * rounded to the penny, and from then on earns interest itself.
 *
 * Rates carry the date they apply from, so a rate change affects only the
 * days after it. Nothing is stored: interest is recomputed with the rest of
 * the projection, and an earlier day's interest can't change because a later
 * rate was added.
 */

const DAYS_PER_YEAR = 365

/** A year's interest on `balance` at these bands. Nothing on a zero or overdrawn balance. */
export function annualInterest(balance: number, bands: InterestBand[]): number {
  if (balance <= 0) return 0
  let total = 0
  let floor = 0
  for (const band of bands) {
    const ceiling = band.upTo ?? Infinity
    const slice = Math.min(balance, ceiling) - floor
    if (slice > 0) total += (slice * band.rate) / 100
    if (balance <= ceiling) break
    floor = ceiling
  }
  return total
}

export function dailyInterest(balance: number, bands: InterestBand[]): number {
  return annualInterest(balance, bands) / DAYS_PER_YEAR
}

/** Looks up the rate in force on a given day. Built once per projection, asked once per day. */
export function rateLookup(rates: InterestRate[]): (iso: string) => InterestBand[] | undefined {
  const sorted = sortRates(rates)
  return (iso) => {
    let current: InterestBand[] | undefined
    for (const rate of sorted) {
      if (rate.from && rate.from > iso) break
      current = rate.bands
    }
    return current
  }
}

/** Oldest first; the undated starting rate comes before any change. */
export function sortRates(rates: InterestRate[]): InterestRate[] {
  return [...rates].sort((a, b) => (a.from ?? '').localeCompare(b.from ?? ''))
}

/** The rate in force on a day, for showing rather than calculating. */
export function rateOn(rates: InterestRate[], iso: string): InterestRate | undefined {
  return sortRates(rates)
    .filter((r) => !r.from || r.from <= iso)
    .pop()
}

/** The payment date in a month — the chosen day, or the month's last if it's shorter. */
function paymentDateIn(year: number, month0: number, day: number | 'last'): Date {
  const last = new Date(year, month0 + 1, 0).getDate()
  return new Date(year, month0, day === 'last' ? last : Math.min(day, last))
}

export function isPaymentDate(date: Date, day: number | 'last'): boolean {
  return paymentDateIn(date.getFullYear(), date.getMonth(), day).getDate() === date.getDate()
}

/** The last payment date strictly before `date`. */
function paymentDateBefore(date: Date, day: number | 'last'): Date {
  const thisMonth = paymentDateIn(date.getFullYear(), date.getMonth(), day)
  return thisMonth < date ? thisMonth : paymentDateIn(date.getFullYear(), date.getMonth() - 1, day)
}

/** The account's interest settings if they would actually pay anything, else undefined. */
export function activeInterest(interest: InterestSettings | undefined): InterestSettings | undefined {
  return interest?.enabled && interest.rates.length > 0 ? interest : undefined
}

/**
 * Interest already accrued when the projection starts.
 *
 * The starting balance is a snapshot partway through an interest period, and
 * the days of that period before it have earned interest that will be paid on
 * the next payment day. There's no record of what the balance was on those
 * days, so they're estimated at the starting balance — close for a savings
 * account, which rarely moves much within a month, and much closer than
 * counting them as nothing and under-paying the first month.
 */
export function accruedBeforeAnchor(
  interest: InterestSettings,
  anchor: Date,
  startingBalance: number,
): number {
  const lookup = rateLookup(interest.rates)
  let accrued = 0
  // The period that the first payment on or after the anchor settles began
  // at the payment before it. If the anchor is itself a payment day, that's a
  // whole period ending the day before.
  const periodStart = paymentDateBefore(
    isPaymentDate(anchor, interest.paymentDay) ? anchor : addDays(anchor, 1),
    interest.paymentDay,
  )
  for (let d = periodStart; d < anchor; d = addDays(d, 1)) {
    const bands = lookup(toISODate(d))
    if (bands) accrued += dailyInterest(startingBalance, bands)
  }
  return accrued
}

export function roundToPenny(amount: number): number {
  return Math.round(amount * 100) / 100
}

const ordinal = (n: number) => {
  const tens = n % 100
  if (tens >= 11 && tens <= 13) return `${n}th`
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`
}

export function describePaymentDay(day: number | 'last'): string {
  return day === 'last' ? 'the last day of the month' : `the ${ordinal(day)}`
}

/** "4.5% to £500, then 1.5%" */
export function describeBands(bands: InterestBand[], currencySymbol: string): string {
  const money = (n: number) => `${currencySymbol}${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`
  const pct = (n: number) => `${n.toLocaleString('en-GB', { maximumFractionDigits: 3 })}%`
  if (bands.length === 1) return pct(bands[0].rate)
  return bands
    .map((b, i) => (b.upTo !== undefined ? `${pct(b.rate)} to ${money(b.upTo)}` : i === 0 ? pct(b.rate) : `then ${pct(b.rate)}`))
    .join(', ')
}
